import { Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';
import { useRoles } from '../hooks/roles/RolesContext';
import { PageFallback } from './PageFallback';

/**
 * Who may render a group of routes. UI gating only: RLS and the SQL helpers
 * are the security boundary; this stops people landing on pages that would
 * show them empty data.
 *
 * - staff: any signed-in staff member.
 * - fullAccess: staff who are not LD Assistants (Documents, analytics).
 * - teacher: the teacher review portal.
 * - keystoneMember: keystone_leader, keystone_ld or admin.
 * - keystoneLeader: Keystone Leaders (settings, enrolment management).
 */
export type AccessRule = 'staff' | 'fullAccess' | 'teacher' | 'keystoneMember' | 'keystoneLeader';

const Loading = () => (
  <div className="flex w-full min-h-screen justify-center items-center">Loading...</div>
);

/**
 * One layout route replacing the old StaffRoute, FullAccessRoute,
 * TeacherRoute, RequireKeystoneMember and RequireKeystoneLeader guards. Each
 * rule keeps its guard's exact redirects, including whether it replaced the
 * history entry.
 */
export function RequireAccess({ rule }: { rule: AccessRule }) {
  const { user, loading: authLoading } = useAuth();
  const roles = useRoles();
  const needsRoles = rule === 'fullAccess' || rule === 'keystoneMember' || rule === 'keystoneLeader';

  if (authLoading || (needsRoles && roles.loading)) return <Loading />;

  const isKeystone = rule === 'keystoneMember' || rule === 'keystoneLeader';
  const requiredRole = rule === 'teacher' ? 'teacher' : 'staff';
  if (!user || user.role !== requiredRole) {
    return <Navigate to="/login" replace={isKeystone} />;
  }

  if (rule === 'fullAccess' && !roles.canAccessDocuments) {
    // LD Assistants cannot access these routes.
    return <Navigate to="/staff/case-notes" />;
  }

  if (isKeystone) {
    if (!roles.isKeystoneMember) return <Navigate to="/staff" replace />;
    // Members who aren't Leaders stay in their Keystone context.
    if (rule === 'keystoneLeader' && !roles.isKeystoneLeader) {
      return <Navigate to="/staff/keystone" replace />;
    }
  }

  return (
    <Suspense fallback={<PageFallback />}>
      <Outlet />
    </Suspense>
  );
}
